import React, { useState, useEffect, useCallback } from 'react';
import { ChevronRight, ChevronLeft, Quote } from 'lucide-react';
import { Button } from '../ui/button';
import { scrollToSection } from './scroll';

const ASSET = 'https://customer-assets.emergentagent.com/job_jetco-electrical/artifacts';

const workShowcase = [
  { id: 1, image: `${ASSET}/lexucu1m_20260101_202034.jpg`, title: 'EV Charger Installation', description: 'Tesla Wall Connector professionally installed and ready to charge', service: 'EV Charger Installation', quote: 'Installed our Tesla Wall Connector quickly and professionally. Clean work!', author: 'Michael R., Brooklyn' },
  { id: 3, image: `${ASSET}/1pb2rccd_20251227_115055.jpg`, title: 'Commercial Electrical', description: 'Professional lighting and electrical installation for businesses', service: 'Commercial Electrical', quote: 'Our store lighting looks brighter than ever and the crew worked around our hours.', author: 'Store owner, Flatbush' },
  { id: 4, image: `${ASSET}/ah8bg72s_20251227_115046.jpg`, title: 'Security & Outdoor Lighting', description: 'Commercial security camera and lighting installation', service: 'Commercial Electrical', quote: 'Cameras and outdoor lights went in neat and on schedule. We feel much safer.', author: 'Property manager, Bay Ridge' },
  { id: 5, image: `${ASSET}/grhkz6mv_20251219_140718.jpg`, title: 'Data & Network Wiring', description: 'Professional network rack and structured cabling installation', service: 'Commercial Electrical', quote: 'The cleanest network wiring job we have ever had. Every cable labeled.', author: 'Office manager, Williamsburg' },
];

const slidePosition = (index, current) => {
  if (index === current) return 'opacity-100 translate-x-0';
  return index < current ? 'opacity-0 -translate-x-full' : 'opacity-0 translate-x-full';
};

const arrowClass = 'absolute top-6 sm:top-1/2 sm:-translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 bg-black/50 hover:bg-yellow-400 text-white hover:text-black rounded-full flex items-center justify-center transition-all border border-white/20 hover:border-yellow-400';

const SlideQuote = ({ quote, author }) => (
  <figure className="mt-3 sm:mt-4 max-w-xl border-l-4 border-yellow-400 bg-black/50 backdrop-blur-sm rounded-r-lg px-4 py-3" data-testid="slide-quote">
    <Quote className="w-4 h-4 text-yellow-400 mb-1" aria-hidden="true" />
    <blockquote className="text-white text-sm sm:text-base italic leading-snug line-clamp-3">“{quote}”</blockquote>
    <figcaption className="text-yellow-400 text-sm font-semibold mt-1">— {author}</figcaption>
  </figure>
);

const Slide = ({ slide, position }) => (
  <div className={`absolute inset-0 transition-all duration-700 ease-in-out ${position}`}>
    <img src={slide.image} alt={slide.title} className="w-full h-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent">
      <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-8">
        <div className="inline-block bg-yellow-400 text-black font-bold px-4 py-2 rounded-full text-xs sm:text-sm mb-2 sm:mb-4">{slide.service}</div>
        <h3 className="text-xl md:text-3xl font-bold text-white mb-1 sm:mb-2">{slide.title}</h3>
        <p className="hidden sm:block text-gray-300 text-lg">{slide.description}</p>
        <SlideQuote quote={slide.quote} author={slide.author} />
      </div>
    </div>
  </div>
);

export const ProjectSlideshow = () => {
  const [current, setCurrent] = useState(0);
  const total = workShowcase.length;
  const next = useCallback(() => setCurrent((prev) => (prev + 1) % total), [total]);
  const prev = () => setCurrent((p) => (p - 1 + total) % total);

  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next]);

  return (
    <section className="py-20 bg-white" data-testid="project-slideshow">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our <span className="text-yellow-500">Recent Projects</span></h2>
          <div className="w-24 h-1 bg-yellow-400 mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">Browse through our latest electrical installations across Brooklyn</p>
        </div>
        <div className="max-w-5xl mx-auto">
          <div className="relative rounded-2xl overflow-hidden shadow-xl border-2 border-gray-200">
            <div className="relative aspect-[4/5] sm:aspect-[16/10] overflow-hidden">
              {workShowcase.map((slide, index) => (
                <Slide key={slide.id} slide={slide} position={slidePosition(index, current)} />
              ))}
            </div>
            <button onClick={prev} className={`left-4 ${arrowClass}`} data-testid="slideshow-prev" aria-label="Previous slide"><ChevronLeft className="w-6 h-6" /></button>
            <button onClick={next} className={`right-4 ${arrowClass}`} data-testid="slideshow-next" aria-label="Next slide"><ChevronRight className="w-6 h-6" /></button>
            <div className="absolute top-4 left-1/2 -translate-x-1/2 sm:left-auto sm:translate-x-0 sm:right-4 bg-black/60 text-white px-4 py-2 rounded-full text-sm font-medium" data-testid="slideshow-counter">{current + 1} / {total}</div>
          </div>
          <div className="flex justify-center gap-3 mt-6">
            {workShowcase.map((slide, index) => (
              <button
                key={slide.id}
                onClick={() => setCurrent(index)}
                data-testid={`slideshow-thumb-${slide.id}`}
                className={`w-20 h-14 rounded-lg overflow-hidden border-2 transition-all ${index === current ? 'border-yellow-400 shadow-lg' : 'border-gray-300 hover:border-gray-400 opacity-60 hover:opacity-100'}`}
              >
                <img src={slide.image} alt={slide.title} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Button onClick={() => scrollToSection('contact')} className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold shadow-lg" data-testid="slideshow-estimate-cta">
              Get a Free Estimate for Your Project
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};
